import * as React from 'react';
import { Builder } from '@builder.io/react';
import image from '@/assets/images/cms/carousel.svg?inline';
import { iconNames } from '@/components/shared';
import { TeaserCardComponentProps } from '../TeaserCard/register';
import { createBuilderOnChangeTagsString } from './builderOnChangeTags';

export type TeaserCardSliderSourceMode = 'manual' | 'taggedPages';
export type TeaserCardSliderSortOrder = 'newest' | 'oldest';
export type TeaserCardSliderSlide = TeaserCardComponentProps & { readonly imagePreview?: string };

export interface TeaserCardSliderProps {
	slides?: TeaserCardSliderSlide[];
	sourceMode?: TeaserCardSliderSourceMode;
	tags?: string[];
	sortOrder?: TeaserCardSliderSortOrder;
	maxItems?: number;
}

// Capture the public API key at module-load time. The <script> tag in the HTML
// sets window.__BUILDER_PUBLIC_API_KEY__ synchronously before ES modules run.
const _builderPublicApiKey: string =
	typeof window !== 'undefined' &&
	typeof (window as unknown as Record<string, unknown>).__BUILDER_PUBLIC_API_KEY__ === 'string'
		? ((window as unknown as Record<string, unknown>).__BUILDER_PUBLIC_API_KEY__ as string)
		: '';

const teaserCardSliderContentModel = 'page';
const availableTagsPageSize = 100;
const availableTagsMaxPages = 10;

let availableTagsCache: string[] = [];
let availableTagsFetchPromise: Promise<string[]> | null = null;

const normalizeAvailableTag = (value: unknown): string | null => {
	if (typeof value !== 'string') return null;

	const normalizedValue = value.trim();
	return normalizedValue.length > 0 ? normalizedValue : null;
};

const getAvailableTagsFromEntry = (entry: unknown): string[] => {
	if (typeof entry !== 'object' || entry === null) return [];

	const record = entry as {
		readonly tags?: unknown;
		readonly data?: {
			readonly tags?: unknown;
		};
	};
	const tags = new Set<string>();
	const sources = [record.tags, record.data?.tags];

	for (let i = 0; i < sources.length; i++) {
		const source = sources[i];
		if (!Array.isArray(source)) continue;

		for (let j = 0; j < source.length; j++) {
			const tag = normalizeAvailableTag(source[j]);
			if (tag) {
				tags.add(tag);
			}
		}
	}

	return Array.from(tags);
};

const sortAvailableTags = (tags: readonly string[]): string[] =>
	[...tags].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));

const areSameAvailableTags = (left: readonly string[], right: readonly string[]): boolean => {
	if (left.length !== right.length) return false;

	for (let i = 0; i < left.length; i++) {
		if (left[i] !== right[i]) return false;
	}

	return true;
};

const fetchAvailableTags = async (): Promise<string[]> => {
	if (_builderPublicApiKey.length === 0) return [];

	const tags = new Set<string>();

	for (let pageIndex = 0; pageIndex < availableTagsMaxPages; pageIndex++) {
		const offset = pageIndex * availableTagsPageSize;
		const url =
			'https://cdn.builder.io/api/v3/content/' +
			teaserCardSliderContentModel +
			'?apiKey=' +
			encodeURIComponent(_builderPublicApiKey) +
			'&fields=' +
			encodeURIComponent('tags,data.tags') +
			'&limit=' +
			availableTagsPageSize +
			'&offset=' +
			offset +
			'&includeUnpublished=false' +
			'&cachebust=true';

		const response = await fetch(url);
		if (!response.ok) {
			throw new Error('Failed to fetch tags for TeaserCardSlider (' + response.status + ')');
		}

		const payload = (await response.json()) as { readonly results?: unknown[] };
		const results = Array.isArray(payload.results) ? payload.results : [];

		for (let resultIndex = 0; resultIndex < results.length; resultIndex++) {
			const entryTags = getAvailableTagsFromEntry(results[resultIndex]);
			for (let tagIndex = 0; tagIndex < entryTags.length; tagIndex++) {
				tags.add(entryTags[tagIndex]);
			}
		}

		if (results.length < availableTagsPageSize) {
			break;
		}
	}

	return sortAvailableTags(Array.from(tags));
};

const getAvailableTags = (): Promise<string[]> => {
	if (availableTagsFetchPromise) return availableTagsFetchPromise;

	availableTagsFetchPromise = fetchAvailableTags()
		.then((tags) => {
			availableTagsCache = tags;
			return tags;
		})
		.catch((error: unknown) => {
			console.warn('[TeaserCardSlider] Could not load available tags for Builder suggestions', error);
			availableTagsFetchPromise = null;
			return availableTagsCache;
		});

	return availableTagsFetchPromise;
};

const registerComponent = <TProps extends TeaserCardSliderProps>(
	Component: React.FC<TProps>,
	onChangeStr: string,
	availableTags: readonly string[],
): void => {
	Builder.registerComponent(Component, {
		name: 'TeaserCardSlider',
		image,
		inputs: [
			{
				name: 'sourceMode',
				type: 'enum',
				enum: [
					{ label: 'Manual slides', value: 'manual' },
					{ label: 'Auto-fill from tags', value: 'taggedPages' },
				],
				defaultValue: 'manual',
				required: true,
				onChange: onChangeStr,
			},
			{
				name: 'autoGeneratedSlideKeysJson',
				type: 'string',
				defaultValue: '[]',
				hideFromUI: true,
			},
			{
				name: 'slides',
				type: 'list',
				defaultValue: [],
				required: false,
				helperText: 'Manual slides are always kept. In tag mode, fetched pages are appended automatically.',
				copyOnAdd: false,
				subFields: [
					{
						name: 'image',
						type: 'Bizzkit DAM File',
					},
					{
						name: 'imagePreview',
						type: 'string',
						hideFromUI: true,
					},
					{
						name: 'altText',
						type: 'string',
					},
					{
						name: 'title',
						type: 'string',
						required: true,
					},
					{
						name: 'text',
						type: 'string',
					},
					{
						type: 'string',
						name: 'icon',
						enum: Array.from(iconNames),
					},
					{
						name: 'url',
						type: 'string',
					},
					{
						name: 'ctaLabel',
						type: 'string',
						defaultValue: 'Read More',
					},
					{
						name: 'hideCtaLabel',
						type: 'boolean',
						defaultValue: false,
					},
				],
			},
			{
				name: 'tags',
				type: 'Tags',
				defaultValue: [],
				required: true,
				model: teaserCardSliderContentModel,
				enum: availableTags,
				helperText:
					availableTags.length > 0
						? 'Start typing to choose from existing page tags, or press Enter to create a new one. Pages with one or more of these tags are included in the slider.'
						: 'Type a tag and press Enter. Pages with one or more of these tags are included in the slider.',
				showIf: `options?.get('sourceMode') === 'taggedPages'`,
				onChange: onChangeStr,
			},
			{
				name: 'sortOrder',
				type: 'enum',
				enum: [
					{ label: 'Newest first', value: 'newest' },
					{ label: 'Oldest first', value: 'oldest' },
				],
				defaultValue: 'newest',
				required: true,
			},
			{
				name: 'maxItems',
				type: 'number',
				defaultValue: 12,
				min: 1,
				max: 50,
				helperText: 'Maximum number of cards fetched when using tag auto-fill.',
				showIf: `options?.get('sourceMode') === 'taggedPages'`,
				onChange: onChangeStr,
			},
		],
	});
};

// ──────────────────────────────────────────────────────────────────────────────
// Component registration
// ──────────────────────────────────────────────────────────────────────────────

const register = <TProps extends TeaserCardSliderProps>(Component: React.FC<TProps>): void => {
	// Builder evaluates onChange handlers from source strings, so we prebuild one that inlines the
	// API key and the extracted helper functions from `builderOnChangeTags.ts` instead of passing a closure.
	const onChangeStr = createBuilderOnChangeTagsString(_builderPublicApiKey);
	const initialAvailableTags = availableTagsCache;
	registerComponent(Component, onChangeStr, initialAvailableTags);

	if (typeof window === 'undefined' || _builderPublicApiKey.length === 0) {
		return;
	}

	void getAvailableTags().then((availableTags) => {
		if (areSameAvailableTags(initialAvailableTags, availableTags)) {
			return;
		}

		registerComponent(Component, onChangeStr, availableTags);
	});
};

export default register;
